<script lang="ts">
export default {
  name: 'ExportMenu',
};
</script>

<script setup lang="ts">
import { Download } from '@lucide/vue';
import { computed, ref } from 'vue';
import Toggle from '@/components/v2/Toggle.vue';

const props = withDefaults(
  defineProps<{
    items: string[];
    supportIndividualExports?: boolean;
    disabled?: boolean;
  }>(),
  {
    disabled: false
  });

const emit = defineEmits<{
  (e: 'export', type: string, selected: string[], finishExport: () => void, oneFile?: boolean): void;
}>();

const selected = ref<string[]>([]);
const menuOpen = ref<boolean>(false);
const isExporting = ref<boolean>(false);
const oneFile = ref<boolean>(false);

const exportDisabled = computed(() => selected.value.length === 0);

function selectAll() {
  selected.value = props.items;
}

function clear() {
  selected.value = [];
}

async function exportPNG() {
  if (selected.value.length === 0) {
    return;
  }

  if (props.supportIndividualExports) {
    emit('export', 'png', selected.value, () => (isExporting.value = false), oneFile.value);
    oneFile.value = false;
  } else {
    emit('export', 'png', selected.value, () => (isExporting.value = false));
  }
  selected.value = [];
  isExporting.value = true;
  toggleMenu();
}

function exportPDF() {
  if (selected.value.length === 0) {
    return;
  }

  if (props.supportIndividualExports) {
    emit('export', 'pdf', selected.value, () => (isExporting.value = false), oneFile.value);
    oneFile.value = false;
  } else {
    emit('export', 'pdf', selected.value, () => (isExporting.value = false));
  }
  selected.value = [];
  isExporting.value = true;
  toggleMenu();
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}
</script>

<template>
  <div class="relative flex w-10 flex-col" ref="dropdownTrigger">
    <span v-if="isExporting" class="loading loading-dots loading-xs self-center"></span>
    <div
      v-else
      role="button"
      tabindex="0"
      class="btn btn-xs flex h-7 w-9 self-center border-none bg-primary"
      :class="{'btn-disabled' : props.disabled}"
      @click="toggleMenu">
      <Download :size="15" class="stroke-white" />
    </div>
    <div
      v-if="menuOpen"
      class="menu menu-lg absolute bottom-auto top-full z-90 mt-1 flex w-max origin-top rounded-lg border border-solid border-gray-200 bg-base-100 py-2 shadow">
      <div v-for="(item, index) in props.items" :key="index" class="flex items-center p-2">
        <input type="checkbox" v-model="selected" :value="item" :id="'checkbox-' + index" class="mr-2" />
        <label :for="'checkbox-' + index" class="flex-1">{{ item }}</label>
      </div>
      <div class="flex" v-if="supportIndividualExports">
        <toggle id="one-file" label="Download As One File" v-model="oneFile" />
      </div>
      <div tabindex="0" class="flex justify-end gap-2 p-2">
        <button @click="selectAll" class="btn btn-secondary btn-sm text-white">Select All</button>
        <button @click="clear" class="btn btn-outline btn-primary btn-sm border border-solid">
          Clear
        </button>
        <button @click="exportPNG" :disabled="exportDisabled" class="btn btn-primary btn-sm text-white">
          Export PNG
        </button>
        <button @click="exportPDF" :disabled="exportDisabled" class="btn btn-primary btn-sm text-white">
          Export PDF
        </button>
      </div>
    </div>
  </div>
</template>
